/**
 * Cart One-Click Upsell
 * Handles adding upsell products to cart without page refresh
 */

class CartUpsell {
  constructor() {
    this.init();
  }

  init() {
    // Listen for upsell button clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-upsell-add-btn]')) {
        e.preventDefault();
        this.handleUpsellAdd(e.target.closest('[data-upsell-add-btn]'));
      }
    });
  }

  /**
   * Handle adding upsell product to cart
   * @param {Element} button - The clicked upsell button
   */
  async handleUpsellAdd(button) {
    if (button.disabled) return;

    const variantId = button.getAttribute('data-variant-id');
    const productTitle = button.getAttribute('data-product-title');
    
    if (!variantId) {
      console.error('No variant ID found for upsell product');
      return;
    }

    // Show loading state
    this.setButtonLoading(button, true);

    try {
      // Add to cart via AJAX
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Hide the upsell container since product is now in cart
      const upsellContainer = button.closest('[data-upsell-container]');
      if (upsellContainer) {
        upsellContainer.style.display = 'none';
      }

      // Trigger cart update events
      this.triggerCartUpdate();

      // Show success notification
      this.showSuccessNotification(productTitle);

    } catch (error) {
      console.error('Error adding upsell product to cart:', error);
      this.showErrorNotification('Unable to add product to cart. Please try again.');
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  /**
   * Set loading state for button
   * @param {Element} button - The button element
   * @param {Boolean} loading - Whether to show loading state
   */
  setButtonLoading(button, loading) {
    const buttonText = button.querySelector('.cart__upsell__btn-text');
    const buttonLoader = button.querySelector('.cart__upsell__btn-loader');
    
    if (loading) {
      button.disabled = true;
      if (buttonText) buttonText.style.opacity = '0';
      if (buttonLoader) buttonLoader.style.display = 'block';
    } else {
      button.disabled = false;
      if (buttonText) buttonText.style.opacity = '1';
      if (buttonLoader) buttonLoader.style.display = 'none';
    }
  }

  /**
   * Trigger cart update events for other components
   */
  triggerCartUpdate() {
    // Get the updated cart and trigger refresh
    this.refreshCart();
    
    // Dispatch custom events that other cart components may listen to
    document.dispatchEvent(new CustomEvent('theme:cart:change'));
    document.dispatchEvent(new CustomEvent('theme:product:added'));
    
    // Update cart drawer if it exists
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents();
    }
    
    // Update cart items component if it exists
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      cartItems.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    }

    // Update cart count in header
    this.updateCartCount();
  }

  /**
   * Refresh cart content by fetching updated cart data
   */
  async refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update all price holders with new cart data
      const priceHolders = document.querySelectorAll('[data-cart-price-holder]');
      if (priceHolders.length) {
        const cartResponse = await fetch('/cart?section_id=api-cart-items');
        const cartText = await cartResponse.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cartText;
        
        const newPriceContent = tempDiv.querySelector('[data-api-cart-price]');
        if (newPriceContent) {
          priceHolders.forEach(holder => {
            holder.innerHTML = newPriceContent.innerHTML;
          });
        }
      }
      
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count elements
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
        element.classList.toggle('is-hidden', cart.item_count === 0);
      });

    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Show success notification
   * @param {String} productTitle - The title of the added product
   */
  showSuccessNotification(productTitle) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'cart-upsell-notification cart-upsell-notification--success';
    notification.innerHTML = `
      <div class="cart-upsell-notification__content">
        <span class="cart-upsell-notification__text">${productTitle} added to cart</span>
      </div>
    `;

    // Add to page
    document.body.appendChild(notification);

    // Show notification
    setTimeout(() => {
      notification.classList.add('is-visible');
    }, 100);

    // Hide and remove notification after 3 seconds
    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  /**
   * Show error notification
   * @param {String} message - The error message
   */
  showErrorNotification(message) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'cart-upsell-notification cart-upsell-notification--error';
    notification.innerHTML = `
      <div class="cart-upsell-notification__content">
        <span class="cart-upsell-notification__text">${message}</span>
      </div>
    `;

    // Add to page
    document.body.appendChild(notification);

    // Show notification
    setTimeout(() => {
      notification.classList.add('is-visible');
    }, 100);

    // Hide and remove notification after 5 seconds
    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 5000);
  }
}

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartUpsell();
  });
} else {
  new CartUpsell();
}